import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, X, Sparkles, Play, Pause } from "lucide-react";
import { fetchStories, resolveImage } from "@/lib/api";

const StoryAudio = ({ url, color, storyId }) => {
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const toggle = () => {
    if (playing) {
      ref.current?.pause();
      setPlaying(false);
      return;
    }
    if (!ref.current) {
      ref.current = new Audio(resolveImage(url));
      ref.current.onended = () => setPlaying(false);
    }
    ref.current.play().catch(() => {});
    setPlaying(true);
  };
  return (
    <button
      onClick={toggle}
      data-testid={`story-audio-${storyId}`}
      className="mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3 font-display text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
      style={{ backgroundColor: color }}
    >
      {playing ? <Pause className="h-4 w-4 fill-white" /> : <Play className="h-4 w-4 fill-white" />}
      {playing ? "Pause the story teaser" : "Listen to the story teaser"}
    </button>
  );
};

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    fetchStories().then(setStories).catch(() => {});
  }, []);

  return (
    <section id="stories" className="relative mx-auto flex min-h-[100svh] w-full max-w-7xl flex-col justify-center px-4 py-28 sm:px-6 sm:py-36" data-testid="stories-section">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-xs font-bold uppercase tracking-[0.3em] text-[#16A34A]"
      >
        Discover
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.08 }}
        className="mt-3 max-w-xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
      >
        Big Bible truths, told in little ways.
      </motion.h2>

      <div className="mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 lg:grid lg:grid-cols-5 lg:overflow-visible">
        {stories.map((s, i) => (
          <motion.button
            key={s.story_id}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: i * 0.07 }}
            whileHover={{ y: -8, rotate: i % 2 ? 1 : -1 }}
            onClick={() => setActive(s)}
            data-testid={`story-card-${s.story_id}`}
            className="group w-60 shrink-0 snap-start overflow-hidden rounded-3xl border border-[#E2DDD0] bg-white text-left shadow-[0_10px_30px_-5px_rgba(234,179,8,0.1)] transition-shadow hover:shadow-[0_20px_40px_-10px_rgba(14,165,233,0.25)] lg:w-auto"
          >
            <div
              className="relative flex h-36 items-end p-4"
              style={{ background: `linear-gradient(135deg, ${s.color}22, ${s.color}55)` }}
            >
              <span
                className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full font-display text-lg font-bold text-white shadow-lg transition-transform group-hover:scale-110 group-hover:-rotate-12"
                style={{ backgroundColor: s.color }}
              >
                {s.character[0]}
              </span>
              <BookOpen className="h-8 w-8" style={{ color: s.color }} />
            </div>
            <div className="p-5">
              <h3 className="font-display text-lg font-bold leading-tight text-slate-900">{s.title}</h3>
              <p className="mt-1 text-xs font-bold uppercase tracking-wider" style={{ color: s.color }}>
                {s.lesson}
              </p>
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/50 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="story-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.85, y: 40, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
              data-testid="story-modal"
            >
              <div className="p-8" style={{ background: `linear-gradient(135deg, ${active.color}18, ${active.color}40)` }}>
                <button
                  onClick={() => setActive(null)}
                  data-testid="story-modal-close"
                  aria-label="Close story"
                  className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-white/80 text-slate-600 transition-transform hover:scale-110"
                >
                  <X className="h-4 w-4" />
                </button>
                <span
                  className="grid h-16 w-16 place-items-center rounded-full font-display text-2xl font-bold text-white shadow-xl"
                  style={{ backgroundColor: active.color }}
                >
                  {active.character[0]}
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold text-slate-900">{active.title}</h3>
                <p className="mt-1 text-sm font-bold uppercase tracking-wider" style={{ color: active.color }}>
                  {active.verse}
                </p>
              </div>
              <div className="p-8 pt-6">
                <p className="text-base text-slate-600 leading-relaxed">{active.blurb}</p>
                <div className="mt-5 flex items-center gap-2 rounded-2xl bg-[#F8F5EC] px-4 py-3">
                  <Sparkles className="h-4 w-4 shrink-0 text-[#FFC107]" />
                  <p className="text-sm font-semibold text-slate-600">
                    Lesson: {active.lesson}
                  </p>
                </div>
                {active.video_url && (
                  <video
                    src={resolveImage(active.video_url)}
                    controls
                    autoPlay
                    playsInline
                    className="mt-4 w-full rounded-2xl"
                    data-testid="story-video"
                  />
                )}
                {active.audio_url && (
                  <StoryAudio url={active.audio_url} color={active.color} storyId={active.story_id} />
                )}
                <p className="mt-4 text-xs text-slate-400">
                  Full animated episodes arrive with launch — the teaser above is a real taster.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Stories;
